import { ChangeEvent, FC, HTMLAttributes, ReactElement, useEffect, useState } from "react";
import { Autocomplete, AutocompleteChangeReason, AutocompleteRenderInputParams, TextField } from "@mui/material";
import { useIntl } from "react-intl";
import { Controller, get, useFormContext, useWatch } from "react-hook-form";

export type IDropdownInputProps = {
  name: string;
  label?: string | number | ReactElement;
  placeholder?: string;
  options: Array<IAutocompleteOption>;
  variant?: "standard" | "filled" | "outlined";
  helperText?: string;
  disabled?: boolean;
};

export interface IAutocompleteOption {
  id: string | number;
  title: string;

  [key: string]: string | number;
}

export const DropdownInput: FC<IDropdownInputProps> = props => {
  const { options, label, placeholder, name, variant = "outlined", helperText, disabled } = props;

  const [open, setOpen] = useState(false);

  const form = useFormContext<any>();

  const error = get(form.formState.errors, name);
  const value = useWatch({ name });

  const { formatMessage } = useIntl();
  const localizedLabel = label === void 0 ? "" : label;
  const localizedHelperText = error ? formatMessage({ id: error.message }, { label: localizedLabel }) : "";

  useEffect(() => {
    return () => {
      form.resetField(name);
    };
  }, []);

  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => (
        <Autocomplete
          open={open}
          onOpen={() => !disabled && setOpen(true)}
          onClose={() => setOpen(false)}
          disabled={disabled}
          sx={{ my: 1 }}
          multiple={false}
          options={options}
          freeSolo
          autoSelect
          value={options.find((option: IAutocompleteOption) => value === option.title) || null}
          onChange={(
            _event: ChangeEvent<unknown>,
            option: string | IAutocompleteOption | null,
            reason: AutocompleteChangeReason,
          ): void => {
            let value = option ? (typeof option !== "string" ? option.title : option) : null;
            if (reason === "blur" && typeof option === "string") {
              const foundOption = options.find(({ title }) => option === title);
              value = foundOption?.title || option;
            }
            form.setValue(name, value, { shouldDirty: true, shouldTouch: true });
            void form.trigger(name);
          }}
          getOptionLabel={(option: IAutocompleteOption | string) =>
            typeof option !== "string" ? option.title : option
          }
          renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IAutocompleteOption | string) => {
            const title = typeof option !== "string" ? option.title : option;
            return (
              <li {...props} key={typeof option !== "string" ? option.id : option}>
                {title}
              </li>
            );
          }}
          renderInput={(params: AutocompleteRenderInputParams): ReactElement => (
            <TextField
              {...params}
              {...field}
              inputProps={{ ...params.inputProps }}
              InputProps={{ ...params.InputProps }}
              label={localizedLabel}
              placeholder={placeholder}
              error={!!error || !!helperText}
              helperText={helperText || localizedHelperText}
              variant={variant}
              onChange={() => {}}
              fullWidth
            />
          )}
        />
      )}
    />
  );
};
